import type { FC, DragEvent, ReactNode, ChangeEvent, RefObject } from 'react';
import React, { useState, useEffect } from 'react';
import UploadFlow from './UploadFlow/UploadFlow.lazy';
import { useTranslation } from 'react-i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import styles from './FileUpload.module.scss';
import FileAlreadyExist from '../modals/FileAlreadyExist/FileAlreadyExist.lazy';
import ModalService from '../../services/ModalService';
import { FileAlreadyExistModal } from '../modals/FileAlreadyExist/constants';
import DocumentService, { RootFolderDisplayName } from '../../services/documents/DocumentService';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';
import { UploadFileLocation } from '../../models/Upload';
import type { FileAttachment, DocumentResponseItems, DocumentsResponse } from '../../models/Document';
import { FileType } from '../../models/Document';
import Toast from '../common/toast/Toast';
import { InitialToastValues } from '../../models/Toast'
import type { ToastProps } from '../../models/Toast';
import RenameExistingFile from '../modals/RenameExistingFile/RenameExistingFile.lazy';
import { RenameExistingFileModal } from '../modals/RenameExistingFile/constants';
import SecurityAdminService from '../../services/SecurityAdmin';
import progressStyles from '../../theme/loadingProgress.module.scss';
import { FileIsTooLargeModal } from '../modals/FileTooLarge/constants';
import FileTooLarge from '../modals/FileTooLarge/FileTooLarge.lazy';
import { MegabyteConverter } from '../../services/UtilityService';
import type { FileTypes } from '../../models/FileTypes';
import ApplyToAll from '../modals/ApplyToAll/ApplyToAll.lazy';
import { ApplyToAllModal } from '../modals/ApplyToAll/constants';

interface UploadProps {
  children?: ReactNode
  fileInputRef?: RefObject<HTMLInputElement>
  uploadDestination?: UploadFileLocation
  disableDragging?: boolean
  fileType?: FileTypes
}

export const MaxAttachmentsSize: number = 20; // in Mb

// Reusable upload component
const FileUpload: FC<UploadProps> = ({
  children, fileInputRef, uploadDestination, disableDragging, fileType
}: UploadProps) => {
  const { t }: UseTranslationResponse<'translation', undefined> = useTranslation();
  const [dragging, setDragging] = useState(false);
  const [showFlow, setShowFlow] = useState<boolean>(false);
  const [fileList, setFileList] = useState<File[]>([]);
  const [currentFile, setFile] = useState<File | null>(null);
  const [changedFileName, setChangedFileName] = useState<string | null>(null);
  const [watermarkAvailable, setWatermarkAvaiable] = useState<boolean>(false);
  const [isFetching, setFetching] = useState<boolean>(false);
  const [numberOfFiles, setNumberOfFiles] = useState<number>(0);
  const [currentFileNumber, setCurrentFileNumber] = useState<number>(0);
  const [applyToAll, setApplyToAll] = useState<boolean>(false);
  const [applyToAllAsked, setApplyToAllAsked] = useState<boolean>(false);

  const [toastStatus, setToastStatus] = useState<ToastProps>(InitialToastValues);
  const uploadFiles: FileAttachment[] = useSelector((state: RootState) => state.uploadFiles.files);

  useEffect(() => {
    SecurityAdminService.fetchSecurityLists();
  }, []);

  useEffect(() => {
    if (fileList.length > 0) {
      handleNextFile();
    }
  }, [fileList, numberOfFiles])

  const handleCloseToast = (): void => {
    setToastStatus(InitialToastValues);
  }

  const handleShowError = (message: string): void => {
    setToastStatus({
      message: t(message),
      type: 'error',
      isShown: true
    });
  }

  const handelSetShowFlow = (): void => {
    setShowFlow(true);
  }

  const handleStopShowFlow = (): void => {
    setShowFlow(false);
  }

  const handleSavingFile = (): void => {
    handleStopShowFlow();
    setFetching(true);
  }

  const handleClose = (): void => {
    setFile(null);
    setNumberOfFiles(0);
    setCurrentFileNumber(0);
  }

  const handleNextFile = (): void => {
    handleStopShowFlow();
    setFetching(false);
    if (fileList.length > 0) {
      const file: File | undefined = fileList.shift();
      if (file) {
        handleFileChange(file);
      }
    } else {
      handleClose();
    }
  }

  /**
   * move onto next file in list
   * @param file
   */
  const handleFileChange = (file: File): void => {
    if (!file) {
      return;
    }
    setCurrentFileNumber(prevFileNumber => prevFileNumber + 1);
    checkApplyToAll(file);
  }

  const checkApplyToAll = (file: File): void => {
    if (currentFileNumber > 0 && !applyToAllAsked) {
      ModalService.openCustomModal(ApplyToAllModal, {
        heading: 'upload.applyToAll.heading',
        content: 'upload.applyToAll.content',
        confirmButton: 'action.yes',
        cancel: 'action.no',
        callback: (apply: boolean) => {
          setApplyToAll(apply);
          setApplyToAllAsked(true);
          void handleFileChecks(file);
        }
      });
    } else {
      void handleFileChecks(file);
    }
  }

  /**
   * handle checks for file
   * check if file exists already or if file is too large to be uploaded
   * @param file
   */
  const handleFileChecks = async (file: File) => {
    const MaxSizeInMegs: number = MaxAttachmentsSize * MegabyteConverter;
    const fileName: string = file.name;
    if (uploadDestination === UploadFileLocation.Compose &&
      uploadFiles.length > 0 && uploadFiles.some((uploadedFile: FileAttachment) => uploadedFile.fileName === fileName)) {
      setToastStatus({
        message: t('compose.attachment.exists', { fileName }),
        type: 'warning',
        isShown: true
      });
      handleNextFile();
    } else if (file.size > MaxSizeInMegs) {
      /**
       * if file is too big let user know and move onto next file
       */
      ModalService.openCustomModal(FileIsTooLargeModal, {
        heading: 'upload.fileIsTooLarge.title',
        content: t('upload.fileIsTooLarge.content').toString().replace('{size}', MaxAttachmentsSize.toString()),
        confirmButton: 'upload.fileIsTooLarge.ok',
        callback: () => {
          handleNextFile();
        }
      });
    } else {
      setFile(file);
      setWatermarkAvaiable(file.type === FileType.PDF);

      setFetching(true);
      // CHECK FILE EXISTENCE
      const result: DocumentResponseItems = await DocumentService.fetchDocumentsByName(file.name);
      /**
       * if file with same name exists in backend give the user the option to rename the file
       * if uploading to compose the user has an option to reuse the file with the same name
       */
      if (result && result.items.length > 0) {
        let redundantFileId: string = '';
        let i: number = 0;
        const items: DocumentsResponse[] = result.items;
        let parentFolder: DocumentsResponse | null = null;
        while (redundantFileId === '' && i < items.length) {
          if (items[i].properties.id) {
            redundantFileId = items[i].properties.id;
            parentFolder = items[i];
          }
          i++;
        }
        setFetching(false);
        if (parentFolder) {
          const folderName: string = getParentFolder(parentFolder);
          if (uploadDestination === UploadFileLocation.Compose) {
            ModalService.openCustomModal(FileAlreadyExistModal, {
              heading: 'upload.fileAlreadyExists.title',
              content: 'upload.fileAlreadyExists.compose.content',
              confirmButton: 'action.yes',
              cancel: 'upload.fileAlreadyExists.compose.no',
              metaData: [result.items[0].properties.fileName, folderName, redundantFileId],
              callback: (changedFileName: string, continueWithFile: boolean) => {
                if (continueWithFile) {
                  setChangedFileName(changedFileName);
                  handelSetShowFlow();
                } else {
                  handleNextFile();
                }
              }
            });
          } else {
            ModalService.openCustomModal(RenameExistingFileModal, {
              heading: 'upload.fileAlreadyExists.title',
              content: 'upload.fileAlreadyExists.other.content',
              confirmButton: 'action.ok',
              metaData: [result.items[0].properties.fileName, folderName],
              callback: (changedFileName: string, continueWithFile: boolean) => {
                if (continueWithFile) {
                  setChangedFileName(changedFileName);
                  handelSetShowFlow();
                } else {
                  handleNextFile();
                }
              }
            });
          }
        } else {
          setChangedFileName(file.name);
          handelSetShowFlow();
        }
      } else {
        setFetching(false);
        setChangedFileName(file.name);
        handelSetShowFlow();
      }
    }
  }

  const restartUploadProcess = (files: File[]): void => {
    setApplyToAll(false);
    setApplyToAllAsked(false);
    setCurrentFileNumber(0);
    setFileList(files);
    setNumberOfFiles(files.length);
  }

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    if (event.target?.files && event.target.files.length) {
      const files: File[] = Array.prototype.slice.call(event.target.files);
      restartUploadProcess(files);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disableDragging) {
      return;
    }
    setDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disableDragging) {
      return;
    }
    setDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disableDragging) {
      return;
    }
    setDragging(false);
    if (event.dataTransfer?.files !== null) {
      const files: File[] = Array.prototype.slice.call(event.dataTransfer.files);
      restartUploadProcess(files);
    }
  };

  return (
    <>
      <Box
      component={'div'}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`${styles.FileUpload} ${(dragging) ? styles.fileDraggingActive : ''}`}
    >
      {isFetching && <CircularProgress className={progressStyles.circularProgress}/> }
      <input type='file' hidden ref={fileInputRef} multiple onChange={handleFileUpload}/>
      {children}
    </Box>
    <UploadFlow
      open={showFlow}
      successButton={t('action.upload')}
      file={currentFile}
      changedFileName={changedFileName}
      watermarkAvailable={watermarkAvailable}
      onError={handleShowError}
      handleCloseToast={handleCloseToast}
      uploadDestination={uploadDestination}
      handleNextFile={handleNextFile}
      numberOfFiles={numberOfFiles}
      currentFileNumber={currentFileNumber}
      handleSavingFile={handleSavingFile}
      preDefinedFileType={fileType}
      applyToAll={applyToAll}
    />
      <FileAlreadyExist />
      <FileTooLarge />
      <RenameExistingFile />
      <ApplyToAll />
      <Toast open={toastStatus.isShown} onClose={handleCloseToast} type={toastStatus.type} title={toastStatus.message}/>
    </>
  );
};

export default FileUpload;

/**
 * get the parent folder for the most recent file of the same name
 * @param item
 */
function getParentFolder (item: DocumentsResponse): string {
  let folderName: string = RootFolderDisplayName;
  const parentFolders = item.properties.parentPath ?? [];
  if (parentFolders.length > 0) {
    const NumberOfParentFolders: number = parentFolders.length;
    const LastParent: string[] = parentFolders[NumberOfParentFolders - 1]; // most recent parent
    const FolderNameIndex = LastParent.length - 1; // index for the name of the folder (last index in array)
    folderName = LastParent[FolderNameIndex];
  }
  return folderName;
}
